import React from "react";
import { Link } from "react-router-dom";
import ArticleLayout, { H2, DataTable } from "./ArticleLayout";

export default function ArticleSpending1pct() {
  return (
    <ArticleLayout
      meta={{
        slug: "one-percent-more-spending-study",
        hub: "Original Research",
        time: "8 min read",
        title: "What 1% More Annual Spending Does to a 35-Year Retirement: A 45,000-Simulation Study | Centennial",
        seoDescription:
          "Original study: 45,000 Monte Carlo simulations show each extra 1% of annual spending costs progressively more — from a 1.8-point success drop at low rates to an 18-point collapse between 6% and 7%. Spending risk compounds.",
        h1: "What 1% More Annual Spending Does to a 35-Year Retirement",
        hook: "Everyone knows spending more is riskier. Almost nobody knows the risk isn't linear — the same extra $10,000 a year can cost 2 points of safety, or 18. We measured exactly where it turns.",
        quickAnswer:
          "In our 45,000-simulation study of a 35-year retirement ($1M at 65, 60/35/5 portfolio, planning to age 100), each additional 1% of annual spending costs progressively more: going from 3% to 4% withdrawals costs just 1.8 points of success (99.9%→98.1%), 4% to 5% costs 7.3 points, 5% to 6% costs 16.1 points, and 6% to 7% costs 18.3 points — down to a 56.4% coin flip. On $1M, each of those steps is the same $10,000/year; the danger it buys is wildly different.",
      }}
    >
      <H2>The experiment</H2>
      <p>
        We took one retiree — age 65, $1,000,000, 60% stocks / 35% bonds / 5% cash, planning to
        age 100 (a 35-year horizon) — and changed exactly one thing: annual inflation-adjusted
        spending, from $30,000 to $70,000 in $5,000 steps. Nine spending levels × 5,000 Monte
        Carlo simulations = 45,000 simulated retirements on the free{" "}
        <Link className="text-[#2B5339] underline" to="/">Centennial engine</Link> (assumptions
        documented on the <Link className="text-[#2B5339] underline" to="/methodology">methodology page</Link>).
      </p>

      <H2>The full curve</H2>
      <DataTable
        head={["Annual spending", "Rate", "Success to 100", "Median at 100", "Median ruin age (failures)"]}
        rows={[
          ["$30,000", "3.0%", "99.9%", "$4.74M", "—"],
          ["$35,000", "3.5%", "99.4%", "$4.18M", "—"],
          ["$40,000", "4.0%", "98.1%", "$3.62M", "—"],
          ["$45,000", "4.5%", "95.4%", "$3.02M", "—"],
          ["$50,000", "5.0%", "90.8%", "$2.45M", "~93"],
          ["$55,000", "5.5%", "84.3%", "$1.89M", "~92"],
          ["$60,000", "6.0%", "74.7%", "$1.32M", "~91"],
          ["$65,000", "6.5%", "66.8%", "$0.91M", "~90"],
          ["$70,000", "7.0%", "56.4%", "$0.34M", "~89"],
        ]}
      />

      <H2>The headline: risk compounds, it doesn't add</H2>
      <DataTable
        head={["The same +1% step", "Extra spending on $1M", "Success cost"]}
        rows={[
          ["3% → 4%", "+$10,000/yr", "−1.8 points"],
          ["4% → 5%", "+$10,000/yr", "−7.3 points"],
          ["5% → 6%", "+$10,000/yr", "−16.1 points"],
          ["6% → 7%", "+$10,000/yr", "−18.3 points"],
        ]}
      />
      <p>
        The fourth $10,000 raise is <strong>ten times more dangerous</strong> than the first.
        Why? At low withdrawal rates the portfolio's growth usually outruns spending, so even
        bad sequences recover. Past ~5%, withdrawals start beating growth in average years —
        every market dip digs a hole the portfolio can no longer climb out of (
        <Link className="text-[#2B5339] underline" to="/learn/sequence-risk">sequence-of-returns risk</Link>).
      </p>

      <H2>The flip side: cutting 1% buys outsized safety</H2>
      <p>
        Read the table bottom-up and it becomes hopeful: a retiree at 6% who trims spending by
        $10,000 jumps from 74.7% to 90.8% — sixteen points of safety for about $27 a day. This
        is also why flexible "guardrails" strategies work so well: they make exactly this cut,
        automatically, only in the years after markets fall.
      </p>

      <H2>What to do with this</H2>
      <p>
        Know where you sit on the curve. Below 4%, spending debates are mostly about legacy
        size, not survival — see our companion study,{" "}
        <Link className="text-[#2B5339] underline" to="/learn/planning-to-100-study">how success changes from age 90 to 100</Link>.
        Between 4% and 5%, small trims matter. Above 5%, every $10k/year is a major safety
        decision — model it before you spend it, free, in the{" "}
        <Link className="text-[#2B5339] underline" to="/">calculator</Link> or{" "}
        <Link className="text-[#2B5339] underline" to="/compare">compare two spending levels side by side</Link>.
      </p>

      <H2>Citing this study</H2>
      <p>
        Produced with the free Centennial simulation engine, June 2026. Cite or reproduce the
        tables with a link to this page. Custom scenario runs for journalists and researchers
        via our <Link className="text-[#2B5339] underline" to="/contact">contact page</Link>.
      </p>
    </ArticleLayout>
  );
}
